import type { SupabaseClient } from "@supabase/supabase-js";

export type CreateCheckoutSessionResult =
  | { readonly ok: true; readonly url: string }
  | {
      readonly ok: false;
      readonly reason:
        | "unauthenticated"
        | "invalid-item"
        | "unknown-item"
        | "network"
        | "stripe-failed";
    };

export interface CreateCheckoutSessionParams {
  readonly itemId: string;
  readonly supabase: SupabaseClient;
}

const ERROR_REASON_MAP: Record<
  string,
  Exclude<
    Extract<CreateCheckoutSessionResult, { ok: false }>["reason"],
    "network"
  >
> = {
  unauthenticated: "unauthenticated",
  "invalid-item": "invalid-item",
  "invalid-body": "invalid-item",
  "unknown-item": "unknown-item",
  "stripe-failed": "stripe-failed",
  "stripe-no-url": "stripe-failed",
};

export async function createCheckoutSession(
  params: CreateCheckoutSessionParams,
): Promise<CreateCheckoutSessionResult> {
  const { itemId, supabase } = params;
  try {
    const { data, error } = await supabase.functions.invoke<{
      url?: string;
      error?: string;
    }>("create-checkout-session", {
      body: { itemId },
    });
    if (error) {
      const code =
        data && typeof data === "object" && typeof data.error === "string"
          ? data.error
          : "";
      return {
        ok: false,
        reason: ERROR_REASON_MAP[code] ?? "network",
      };
    }
    if (!data || typeof data.url !== "string" || data.url.length === 0) {
      return { ok: false, reason: "stripe-failed" };
    }
    return { ok: true, url: data.url };
  } catch {
    return { ok: false, reason: "network" };
  }
}
